"use client";

import { useCallback } from "react";
import { fetchWithTokenRefresh } from "@/core/utils/http/fetch-with-token-refresh";
import { parseBlocks } from "../schemas/blocks";
import type { Message } from "../types";
import type { FormBlockHandlers } from "../components/form-block";

/** Identidade de contato do solicitante (dados do usuário logado). */
export interface Requester {
  nome: string;
  email?: string;
  telefone?: string;
}

interface UseSendBlockActionArgs {
  appUserId?: number | string;
  companyId?: number | string;
  requester: Requester;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setIsLoading: (value: boolean) => void;
}

/** Fallback quando a ação não pôde ser registrada (erro HTTP ou de rede). */
const BLOCK_ACTION_ERROR_TEXT =
  "Não consegui registrar agora. Tente novamente em instantes.";

function generateId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * Envia um `block_action` (submit de `form`) para POST /api/agent/block-action e adiciona a
 * resposta do agente como uma nova mensagem do bot (ST-5). Diferente do chat normal, é uma
 * ação estruturada e discreta — não usa streaming.
 *
 * A identidade do solicitante (nome/email/telefone do usuário logado) vai no `block_action`
 * (dados de contato). O `company_id`/tenant é revalidado no backend pelo JWT.
 */
export function useSendBlockAction({
  appUserId,
  companyId,
  requester,
  setMessages,
  setIsLoading,
}: UseSendBlockActionArgs): FormBlockHandlers {
  const onSubmit = useCallback<NonNullable<FormBlockHandlers["onSubmit"]>>(
    async ({ formId, blockId, actionToken, values }) => {
      const botMessageId = generateId();
      setMessages((prev) => [
        ...prev,
        {
          id: botMessageId,
          content: "Registrando...",
          isUser: false,
          timestamp: new Date(),
          isTyping: true,
        } as Message,
      ]);
      setIsLoading(true);

      try {
        const baseUrl =
          import.meta.env.VITE_AGENT_BASE_URL ||
          import.meta.env.VITE_API_GATEWAY_URL ||
          "";
        const response = await fetchWithTokenRefresh(
          `${baseUrl}/api/agent/block-action`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: String(appUserId ?? ""),
              company_id: String(companyId ?? ""),
              block_action: {
                form_id: formId,
                block_id: blockId,
                action_token: actionToken,
                values,
                requester,
              },
            }),
          },
        );
        // Corpo ilegível (resposta vazia/HTML de proxy) não deve derrubar o fluxo: cai no
        // fallback de erro abaixo, preservando o form editável.
        const data = await response.json().catch(() => null);
        const blocks = parseBlocks(data?.blocks);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === botMessageId
              ? {
                  ...m,
                  // Num erro HTTP sem `response`, o texto genérico é melhor que bolha vazia.
                  content:
                    data?.response ??
                    (response.ok ? "" : BLOCK_ACTION_ERROR_TEXT),
                  blocks,
                  isTyping: false,
                }
              : m,
          ),
        );
        // Sucesso exige as DUAS condições:
        // - HTTP ok: um 5xx com corpo JSON travava o form em "Enviado ✓" sem nada ter sido
        //   registrado. Contrato: erro de validação vem 200 + form reemitido; 4xx/5xx é falha.
        // - sem form reemitido: a reemissão com `errors` é justamente o canal de erro de
        //   validação, e mantém o form original editável.
        // Em falha, o action_token (estável por instância) torna o reenvio seguro: se a
        // primeira tentativa chegou, o backend deduplica.
        const ok = response.ok && !blocks.some((b) => b.type === "form");
        return { ok };
      } catch {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === botMessageId
              ? {
                  ...m,
                  content: BLOCK_ACTION_ERROR_TEXT,
                  isTyping: false,
                }
              : m,
          ),
        );
        return { ok: false };
      } finally {
        setIsLoading(false);
      }
    },
    [appUserId, companyId, requester, setMessages, setIsLoading],
  );

  // ST-5.1: "Melhorar com IA" — POST assist, recebe um block_patch e devolve {field, value}
  // para o FormBlockView aplicar no campo. Não submete o form. Falha → sem alteração.
  const onAssist = useCallback<NonNullable<FormBlockHandlers["onAssist"]>>(
    async ({ formId, blockId, action, targetField, currentValue, context }) => {
      try {
        const baseUrl =
          import.meta.env.VITE_AGENT_BASE_URL ||
          import.meta.env.VITE_API_GATEWAY_URL ||
          "";
        const response = await fetchWithTokenRefresh(
          `${baseUrl}/api/agent/block-action`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: String(appUserId ?? ""),
              company_id: String(companyId ?? ""),
              block_action: {
                form_id: formId,
                block_id: blockId,
                intent: "assist",
                assist: {
                  action,
                  target_field: targetField,
                  current_value: currentValue,
                  context,
                },
              },
            }),
          },
        );
        const data = await response.json();
        const patch = data?.block_patch;
        if (patch && typeof patch.value === "string") {
          return { field: patch.field ?? targetField, value: patch.value };
        }
      } catch {
        // silencioso e não-destrutivo
      }
      return undefined;
    },
    [appUserId, companyId],
  );

  return { onSubmit, onAssist };
}
